import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
/** PUTs the file to the Supabase signed-upload URL with progress, via XHR. */
function uploadToSignedUrl(signedUrl, file, onProgress) {
    // Supabase's signed-upload endpoint expects multipart/form-data with the file
    // under an empty field name + a cacheControl field (mirrors supabase-js). The
    // signed token authorizes the upload, so no apikey header is needed.
    const form = new FormData();
    form.append('cacheControl', '3600');
    form.append('', file);
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', signedUrl);
        xhr.setRequestHeader('x-upsert', 'true');
        // Do NOT set Content-Type: XHR sets the multipart boundary for FormData.
        xhr.upload.onprogress = (e) => {
            if (e.lengthComputable)
                onProgress?.(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300)
                resolve();
            else
                reject(new Error(`Storage ${xhr.status}: ${(xhr.responseText || '').slice(0, 300) || 'no se pudo subir el vídeo'}`));
        };
        xhr.onerror = () => reject(new Error('Error de red al subir el vídeo. Revisa tu conexión e inténtalo de nuevo.'));
        xhr.send(form);
    });
}
export function useRecordings(oppositionId) {
    return useQuery({
        queryKey: ['director-recordings', oppositionId ?? 'all'],
        queryFn: () => {
            const qs = oppositionId ? `?opposition_id=${oppositionId}` : '';
            return apiClient(`/recordings${qs}`);
        },
        staleTime: 1000 * 60,
    });
}
/**
 * Three-step upload: ask the API for a signed URL, PUT the video directly to
 * Storage (bypassing the API for the large payload), then register the metadata.
 */
export function useUploadRecording() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (input) => {
            const { signedUrl, filePath } = await apiClient('/recordings/upload-url', {
                method: 'POST',
                body: JSON.stringify({
                    opposition_id: input.opposition_id,
                    file_name: input.file.name,
                    mime_type: input.file.type,
                    file_size_bytes: input.file.size,
                }),
            });
            await uploadToSignedUrl(signedUrl, input.file, input.onProgress);
            return apiClient('/recordings', {
                method: 'POST',
                body: JSON.stringify({
                    opposition_id: input.opposition_id,
                    title: input.title,
                    ...(input.description ? { description: input.description } : {}),
                    file_path: filePath,
                    file_size_bytes: input.file.size,
                    mime_type: input.file.type,
                }),
            });
        },
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['director-recordings'] }),
    });
}
export function useDeleteRecording() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => apiClient(`/recordings/${id}`, { method: 'DELETE' }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['director-recordings'] }),
    });
}
export function useRecordingPermissions(recordingId) {
    return useQuery({
        queryKey: ['recording-permissions', recordingId],
        queryFn: () => apiClient(`/recordings/${recordingId}/permissions`),
        enabled: !!recordingId,
    });
}
export function useGrantRecordingPermission(recordingId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (studentId) => apiClient(`/recordings/${recordingId}/permissions`, {
            method: 'POST',
            body: JSON.stringify({ student_id: studentId }),
        }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['recording-permissions', recordingId] }),
    });
}
export function useRevokeRecordingPermission(recordingId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (studentId) => apiClient(`/recordings/${recordingId}/permissions/${studentId}`, { method: 'DELETE' }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['recording-permissions', recordingId] }),
    });
}
//# sourceMappingURL=useRecordingsAdmin.js.map